import React, { Fragment, useMemo } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  OnDragEndResponder,
} from 'react-beautiful-dnd';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import {
  compoundMeasureUserAliasIdSelector,
  measuresSelector,
} from '../../../../../redux/selectors/builder';
import { AliasHighlight } from '../../../../../components/Builder/AliasNavigation/AliasHighlight';
import {
  RowType,
  swapQueryMeasure,
} from '../../../../../redux/slices/oldBuilder';
import { MAX_MEASURE_ROWS } from '../../../../../config/constants';
import * as BuilderUI from '../../../../../components/Builder/BuilderUI';
import MeasureRow from '../../../../../components/Builder/Measures/MeasureRow';
import { BuilderAddRowAction } from '../../../../../components/Builder/BuilderActions';
import DndDroppableZone from '../../../../../components/DndEntity/DndDroppableZone';
import { EntityDropzoneType } from '../../../../../components/QuickAccessDictionary/DictionaryTabs/types';
import { useDndEntityInternalContext } from '../../../../../components/DndEntity';
import DropzoneArea from '../../../../../components/DndEntity/DndDroppableZone/DropzoneArea';
import {
  EntityDropzoneDataTypeEnum,
  IEventEntityDropzoneDataType,
  IMeasureEntityDropzoneDataType,
} from '../../../../../components/DndEntity/validation';
import { isEmptyEventConditions } from '../../../../../utils/builder';
import { getDndActiveItemData } from '../../../../../components/DndEntity/utils';

const QueryBuilderMeasures = () => {
  const dispatch = useAppDispatch();

  const { activeItem } = useDndEntityInternalContext();

  const compoundMeasureUserAliasId = useAppSelector(
    compoundMeasureUserAliasIdSelector,
  );

  const onDragEnd: OnDragEndResponder = (result) => {
    if (!result || !result.source || !result.destination) {
      return;
    }

    dispatch(
      swapQueryMeasure({
        sourceIndex: result.source.index,
        targetIndex: result.destination.index,
      }),
    );
  };

  const measures = useAppSelector(measuresSelector);

  // KB-6070: in case of rolling measure, we will not allow user to add
  // more measures
  const canAddMeasure =
    measures.length < MAX_MEASURE_ROWS &&
    !measures.some((row) => row.measureRollingControl);

  const canDropMeasure = useMemo(() => {
    if (activeItem && canAddMeasure) {
      const allEventConditionsFilled = measures.every((measure) => {
        return !isEmptyEventConditions(measure.eventConditions);
      });
      const data = getDndActiveItemData(activeItem);
      if (data && allEventConditionsFilled) {
        return data.__typename === 'TagLeafSimpleMeasure';
      }
    }
    return false;
  }, [activeItem, canAddMeasure, measures]);

  const canDropEvent = useMemo(() => {
    if (activeItem && canAddMeasure) {
      const allEventConditionsFilled = measures.every((measure) => {
        return !isEmptyEventConditions(measure.eventConditions);
      });
      const data = getDndActiveItemData(activeItem);
      if (data && allEventConditionsFilled) {
        return (
          data.__typename === 'TagLeafAnyEvent' ||
          data.__typename === 'TagLeafBasicEvent' ||
          data.__typename === 'TagLeafVirtualEvent'
        );
      }
    }
    return false;
  }, [activeItem, canAddMeasure, measures]);

  const measuresDropzoneId = `New-Measure-Row-Droppable-${measures.length}`;
  const eventsDropzoneId = `New-Measure-Row-Event-Droppable-${measures.length}`;

  return (
    <>
      <AliasHighlight aliasId={compoundMeasureUserAliasId}>
        <DragDropContext onDragEnd={onDragEnd}>
          <BuilderUI.SectionColumn>
            <Droppable droppableId={RowType.measures} direction="vertical">
              {(provided) => (
                <BuilderUI.SectionColumn
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {measures.map((row, rowIndex) => (
                    <Fragment key={`query-builder-measure-${rowIndex}`}>
                      <Draggable
                        isDragDisabled={measures.length === 1}
                        draggableId={RowType.measures + rowIndex}
                        index={rowIndex}
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                          >
                            <MeasureRow
                              aliasId={row.userAliasId}
                              rowMeta={{
                                rowIndex,
                                rowType: RowType.measures,
                              }}
                              dragHandleProps={provided.dragHandleProps}
                            />
                          </div>
                        )}
                      </Draggable>
                    </Fragment>
                  ))}
                  {provided.placeholder}
                </BuilderUI.SectionColumn>
              )}
            </Droppable>
            {canAddMeasure && !canDropMeasure && !canDropEvent && (
              <BuilderAddRowAction
                rowMeta={{
                  rowType: RowType.measures,
                  rowIndex: measures.length - 1,
                }}
                label={'+ Add Measure'}
                trackContext="builder"
                trackAction="add measure"
              />
            )}
            {canDropMeasure && (
              <DndDroppableZone
                accepts={[EntityDropzoneType.Measures]}
                id={measuresDropzoneId}
                data={
                  {
                    id: measuresDropzoneId,
                    type: EntityDropzoneDataTypeEnum.Measures,
                    rowMeta: {
                      rowType: RowType.measures,
                      rowIndex: measures.length,
                    },
                  } satisfies IMeasureEntityDropzoneDataType
                }
              >
                <DropzoneArea size="large">+ Add New Measure</DropzoneArea>
              </DndDroppableZone>
            )}
            {canDropEvent && (
              <DndDroppableZone
                accepts={[EntityDropzoneType.Events]}
                id={eventsDropzoneId}
                data={
                  {
                    id: eventsDropzoneId,
                    type: EntityDropzoneDataTypeEnum.Events,
                    rowMeta: {
                      rowType: RowType.measures,
                      rowIndex: measures.length,
                      eventIndex: 0,
                    },
                    isNewRow: true,
                    shouldAddMeasureFunction: true,
                  } satisfies IEventEntityDropzoneDataType
                }
              >
                <DropzoneArea size="large">
                  + Add Event as New Measure
                </DropzoneArea>
              </DndDroppableZone>
            )}
          </BuilderUI.SectionColumn>
        </DragDropContext>
      </AliasHighlight>
    </>
  );
};

export default QueryBuilderMeasures;
